import {
  type InteracAddress,
  INTERAC_EMAIL_VERIFICATION_STATUSES,
  INTERAC_VERIFICATION_TYPES,
} from "@/hooks/firestore/models/interac/interacAddress";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { attempt } from "@/utilities/attempt";
import type { InteractDeposit } from "../hooks/interac/useInteracDeposits";

export const NOT_REGISTERED_FOR_AUTODEPOSIT_CODE =
  "not-registered-for-autodeposit";

const RETURN_URL_TTL_MS = 30 * 60 * 1000;

type StoredReturnUrl = { url: string; ts: number };

const RETURN_URL_KEY_PREFIX = "interac-return-url:";

const returnUrlKey = (userId: string) => `${RETURN_URL_KEY_PREFIX}${userId}`;

// sessionStorage, not local: keeps it per-tab so a second tab won't clobber an in-flight flow, and it survives the interac redirect in the same tab.
const store = () =>
  typeof window === "undefined" ? null : window.sessionStorage;

// remember where we were before redirecting out to interac
export const saveInteracReturnUrl = (userId: string, url: string) => {
  const value: StoredReturnUrl = { url, ts: Date.now() };
  store()?.setItem(returnUrlKey(userId), JSON.stringify(value));
};

// read the stored return url without clearing it
export const getInteracReturnUrl = (userId: string): string | null => {
  const raw = store()?.getItem(returnUrlKey(userId));
  if (!raw) {
    return null;
  }

  const storedResult = attempt(() => JSON.parse(raw) as StoredReturnUrl);

  if (
    !storedResult.ok ||
    Date.now() - storedResult.data.ts > RETURN_URL_TTL_MS
  ) {
    return null;
  }
  return storedResult.data.url;
};

// whether this tab has a return url stashed, for any account. the flag is
// needed before auth resolves, which is exactly when the userId key is not yet
// known, and a tab only ever holds one account's flow
export const hasInteracReturnUrl = (): boolean => {
  const storage = store();

  if (!storage) {
    return false;
  }

  return Array.from({ length: storage.length }).some((_, index) =>
    storage.key(index)?.startsWith(RETURN_URL_KEY_PREFIX),
  );
};

// read + clear the stored return url
export const consumeInteracReturnUrl = (userId: string): string | null => {
  const url = getInteracReturnUrl(userId);
  store()?.removeItem(returnUrlKey(userId));
  return url;
};

const formatMobile = (mobile: string) => {
  const mobileWithPlus = mobile.startsWith("+") ? mobile : `+1${mobile}`;
  return formatPhoneNumberIntl(mobileWithPlus) || mobile;
};

export const getInteracBankAccountLabel = (
  interacAddress: InteracAddress,
): string =>
  interacAddress.verificationType === INTERAC_VERIFICATION_TYPES.PHONE &&
  interacAddress.mobile
    ? formatMobile(interacAddress.mobile)
    : interacAddress.email;

export const isInteracAddressVerified = (interacAddress: InteracAddress) => {
  return (
    interacAddress.verificationStatus ===
    INTERAC_EMAIL_VERIFICATION_STATUSES.VERIFIED
  );
};

export const pickInteracWithdrawalAddress = (
  interacAddresses: InteracAddress[],
  chosenWalletId?: string,
): InteracAddress | undefined =>
  interacAddresses.find(
    (address) => address.userExternalWalletId === chosenWalletId,
  ) ??
  interacAddresses.find((address) => address.isDefaultWithdrawalWallet) ??
  interacAddresses.find(isInteracAddressVerified) ??
  interacAddresses[0];

export const getStatusLabel = (status: InteractDeposit["status"]) => {
  switch (status) {
    case "AWAITING_RESPONSE":
      return "Awaiting Response";
    case "PROCESSED":
      return "Processed";
    case "PROCESSING":
      return "Pending";
    case "CANCELLED":
      return "Cancelled";
    case "FAILED":
      return "Failed";
    case "CHARGED_BACK":
      return "Charge Back";
    case "REFUNDED":
      return "Refunded";
    default:
      return status;
  }
};
